import React, { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import useShouldShowApplePay from '../../hooks/useShouldShowApplePay';
import AppPaths from '../../AppPaths';
import { useCartPricing } from '../../contexts/CartPricingContext';
import useCheckoutPaths from '../../hooks/useCheckoutPaths';
import * as events from '../../lib/analytics/events';
import { useCartMode } from '../../lib/cartModes';
import CheckoutContext from '../../lib/CheckoutContext';
import { getDomainConfig } from '../../lib/domainConfig';
import { centsToDollars, centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import * as types from '../../types';
import LoginButton from '../../views/Checkout/components/LoginButton';
import ApplePayCheckout from '../ApplePayCheckout';
import Button from '../Button';
import ErrorMessage, { useError } from '../ErrorMessage';
import { SimpleDismissableModal } from '../modals';
import styles from './CartSummary.module.scss';
import { showNanoVariant } from '../NanoBanner/NanoBanner';
import {
  couponWaivesActivationFee,
  inCartPrepaidMembershipCount,
  inCartS3CollarCount,
} from '../ItemsInCart/CouponLineItemV2';
import classNames from 'classnames';
import { getFiGrowthBook } from '../../lib/growthbook';
import { isInAppShop } from '../../lib/inAppShop';
import { cartActions as storeCartActions } from '../../reducers/storeCart';
import { ReactComponent as IconShipping } from '../../assets/images/icons/icon_shipping.svg';
import { ReactComponent as IconReturns } from '../../assets/images/icons/returns.svg';
import { useLocale, FiCountry } from '../../contexts/LocaleContext';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import { appliedCouponDiscountInCents, isBundleCartItem } from '../../lib/cart';
import { calculatePositionSavings } from '../../lib/positionDiscount';
import useFreeShippingDisplay from '../../hooks/useFreeShippingDisplay';
import useCartHasFreeTrialBundle from '../../hooks/useCartHasFreeTrialBundle';
import useCartHasOnlyFreeTrialBundles from '../../hooks/useCartHasOnlyFreeTrialBundles';
import useCartItemV2Enabled from '../../hooks/useCartItemV2Enabled';
import useRescueRenewalPrice from '../../hooks/useRescueRenewalPrice';
import { showRescueGwaVariant, showRescuePdpRedesign } from '../../views/RescueLanding/RescueBanner';
import { getNanoVariantPetName, hasNanoVariantPetName } from '../../lib/util/petUrlParams';

type OnCheckoutFn = (checkoutCart: types.Cart, cartPricing: types.CartPricing) => void;
interface CartActionProps {
  onCheckOut: OnCheckoutFn;
  showDigitalWallets: boolean;
  allowLogin?: boolean;
}

interface PaymentOptionsProps {
  onCheckOut: OnCheckoutFn;
  showDigitalWallets: boolean;
  digitalWalletsOnly: boolean;
}

export function getCartV2FormattedTotal(cents: number): string {
  const hasNonZeroOnesOrTensDigit = (num: number): boolean => num % 10 !== 0 || Math.floor(num / 10) % 10 !== 0;
  if (hasNonZeroOnesOrTensDigit(cents)) {
    return centsToDollars(cents);
  }
  return centsToDollarsNumber(cents).toFixed(0);
}

/** Pick the appropriate currency formatter based on whether the value is a round dollar amount */
export function getCartV2CurrencyFormattedTotal(
  cents: number,
  currencyFormatter: Intl.NumberFormat,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
): string {
  const dollarValue = centsToDollarsNumber(cents);
  if (dollarValue % 1 === 0) {
    return wholeNumberCurrencyFormatter.format(dollarValue);
  }
  return currencyFormatter.format(dollarValue);
}

export function getCartTestTotalDiscount(cartPricing: types.CartPricing, discounts: 'total' | 'promo_only'): number {
  const { cartItemCredits } = cartPricing;
  let activationFeeDiscount = discounts === 'promo_only' ? 0 : cartPricing.waivedActivationFeesInCents || 0;

  // We want to exclude free trial discounts from this since we don't want to include it as part of a "$ OFF" tag
  // cartItemCredits will only be populated if a discount was applied the physical product SKU or subscription,
  // so this will never include discounts on activation fees.
  const creditDiscount = Object.values(cartItemCredits || []).reduce(
    (acc, credits) =>
      acc +
      credits.reduce((accCredit, credit) => {
        if (credit.creditReason === 'free_trial') {
          return accCredit;
        }

        // We currently waive activation fees through a couple of different methods:
        //  a coupon code and multi-dog discounts.
        // cartPricing.waivedActivationFeesInCents includes both cases, so if we see that a promo code waived
        // the activation fee, we need to subtract it from the total waived activation fees so they don't get
        // counted twice.
        if (discounts === 'total' && credit.creditReason === 'promo_waived_activation_fee') {
          activationFeeDiscount -= credit.creditAmountInCents;
        }

        // If promo only, exclude multi-dog discounts
        if (
          discounts === 'promo_only' &&
          credit.creditReason !== 'promo' &&
          credit.creditReason !== 'promo_waived_activation_fee'
        ) {
          return accCredit;
        }

        return accCredit + credit.creditAmountInCents;
      }, 0),
    0,
  );

  // The above logic would result in total discount of $0 if the promo exclusively waived the activation fee,
  // but we want to display the activation fee waiver amount in those situations.
  // There is probably a more elegant way to adjust the logic, but this should suffice.
  if (creditDiscount === 0 && cartPricing.waivedActivationFeesInCents !== undefined) {
    activationFeeDiscount = cartPricing.waivedActivationFeesInCents;
  }

  // a hardware-first code takes its saving off the tracker's unit price, so it never appears as a
  // credit; the per-item preview carries its share
  const hardwarePromoDiscount = Object.values(cartPricing.hardwareDiscounts ?? {}).reduce(
    (acc, discount) => acc + discount.promoDiscountInCents,
    0,
  );

  const cartTestTotalDiscount =
    (cartPricing.appliedAccountBalanceInCents || 0) +
    (cartPricing.appliedGiftCardAmountInCents || 0) +
    creditDiscount +
    activationFeeDiscount +
    hardwarePromoDiscount;

  return cartTestTotalDiscount;
}

function TotalPrice() {
  const { t } = useTranslation();
  const cartPricing = useCartPricing();
  const { totalInCents, cartItemCredits } = cartPricing;
  const { cart } = useContext(CheckoutContext);
  const cartV2Enabled = showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-1', false);
  // the rescue cart itemizes the discount as its own totals row, so the strikethrough and the
  // "$99 saved" badge would state the same saving a third time on the one line meant to be the
  // plain number the adopter pays
  const rescueGwaVariant = showRescuePdpRedesign();
  const { currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const productSkuMap = useProductSkuMap();

  // calculate position discount savings for the yellow pill
  const positionDiscountSavings = React.useMemo(() => {
    const { positionDiscounts } = cartPricing;
    if (!positionDiscounts) return 0;

    return Object.entries(positionDiscounts).reduce((total, [cartItemId, discount]) => {
      if (!discount.isEnrolled || discount.discountPercent === 0) return total;

      const cartItem = cart.cartItems[cartItemId];
      if (!cartItem || !isBundleCartItem(cartItem)) return total;

      const subscriptionProduct = productSkuMap.get(cartItem.subscriptionLineItem.sku)?.product;
      if (!subscriptionProduct || !types.isModuleSubscriptionProduct(subscriptionProduct)) return total;

      const subscriptionPrice = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);
      return total + calculatePositionSavings(subscriptionPrice, discount.discountPercent);
    }, 0);
  }, [cartPricing, cart.cartItems, productSkuMap, currency]);

  if (cartV2Enabled) {
    const creditDiscount = Object.values(cartItemCredits || []).reduce(
      (acc, credits) =>
        acc +
        credits.reduce((accCredit, credit) => {
          // Exclude cart item credits that we know are from the waived activation fee
          // because we'll look at cartPricing.waivedActivationFeesInCents separately.
          if (credit.creditReason === 'promo_waived_activation_fee') {
            return accCredit;
          }

          return accCredit + credit.creditAmountInCents;
        }, 0),
      0,
    );
    const couponApplied = cart.couponCode !== undefined;
    // Experiment: 'nano99' is a coupon code that applies a discount directly to the pre-paid amount
    // While keeping the activation fee. Force the discount amount to display in the rendered component.
    const elevatedNanoCodeApplied = cart.couponCode === 'nano99';
    const cartItems = Object.values(cart.cartItems);
    const totalCollarCount = inCartS3CollarCount(cartItems);
    const monthToMonthCollarCount = inCartPrepaidMembershipCount(cartItems);
    const activationFeeDiscount = cartPricing.waivedActivationFeesInCents || 0;
    const showActivationFeeDiscount =
      ((couponApplied && couponWaivesActivationFee(cartPricing, cart)) || elevatedNanoCodeApplied) &&
      activationFeeDiscount > 0 &&
      totalCollarCount > 0 &&
      totalCollarCount - monthToMonthCollarCount > 0;
    // The elevated nano code applies a discount to the pre-paid amount, but not the activation fee.
    // backendDiscount = discounts the backend already subtracted from totalInCents
    const backendDiscount = elevatedNanoCodeApplied ? creditDiscount : activationFeeDiscount + creditDiscount;
    const originalTotalInCents = backendDiscount + positionDiscountSavings + totalInCents;
    const totalDiscount = backendDiscount;

    // Ultra's hardware kit is discounted when bundled with a new membership ($299 → $199). Surface
    // that savings on the subtotal too: strike through the pre-discount total and show "$X off". This
    // is display-only — the charged total already uses the discounted hardware price.
    const ultraHardwareDiscountInCents = cartItems.reduce((sum, item) => {
      if (!isBundleCartItem(item)) return sum;
      const product = productSkuMap.get(item.lineItem.sku)?.product;
      if (product?.id !== types.ultraCollarId) return sum;
      const ultra = product as types.IUltraProduct;
      const regular = getLocalizedPricing(ultra.pricingInCents, currency);
      const standalone = getLocalizedPricing(ultra.standalonePricingInCents, currency);
      return sum + Math.max(regular - standalone, 0);
    }, 0);

    const showCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
    // for cart test, exclude activation fee waivers - just show coupon + gift card/account balance
    // position discount is handled separately since it's shown in its own pill
    const cartTestCreditDiscount = Object.values(cartItemCredits || []).reduce(
      (acc, credits) =>
        acc +
        credits.reduce((accCredit, credit) => {
          // exclude activation fee waivers and free trials
          if (credit.creditReason === 'promo_waived_activation_fee' || credit.creditReason === 'free_trial') {
            return accCredit;
          }
          return accCredit + credit.creditAmountInCents;
        }, 0),
      0,
    );
    // Don't include position discount in cartTestTotalDiscount - it's shown separately in the pill
    // and subtracted from the final total display
    const cartTestTotalDiscount =
      (cartPricing.appliedAccountBalanceInCents || 0) +
      (cartPricing.appliedGiftCardAmountInCents || 0) +
      cartTestCreditDiscount;

    const waivedActivationFees = cartPricing.waivedActivationFeesInCents || 0;
    const totalSavingsForBadge =
      cartTestTotalDiscount + positionDiscountSavings + waivedActivationFees + ultraHardwareDiscountInCents;
    // For strikethrough: show original price before all discounts when there are other discounts,
    // or just totalInCents when only position discount applies
    const hasOtherDiscounts = cartTestTotalDiscount > 0 || waivedActivationFees > 0 || ultraHardwareDiscountInCents > 0;
    const hasPositionDiscountSavings = positionDiscountSavings > 0;
    const showStrikethrough = hasOtherDiscounts || hasPositionDiscountSavings;
    // Exclude tax from total since it's displayed separately in TaxPrice
    const taxInCents = cartPricing.taxInCents || 0;
    const totalWithoutTax = totalInCents - taxInCents;
    const strikethroughValue = hasOtherDiscounts
      ? totalWithoutTax + cartTestTotalDiscount + waivedActivationFees + ultraHardwareDiscountInCents
      : totalWithoutTax;
    const displayedFinalPrice = totalWithoutTax - positionDiscountSavings;

    return (
      <div
        className={classNames(styles.nanoTotalPrice, styles.priceData, {
          [styles.rescueSubtotal]: rescueGwaVariant,
        })}
      >
        <div>{t('subtotal')}</div>
        <div className={styles.pricingContainer}>
          {!showCartTest && showActivationFeeDiscount && !rescueGwaVariant && (
            <div className={styles.originalPriceValue}>
              {getCartV2CurrencyFormattedTotal(originalTotalInCents, currencyFormatter, wholeNumberCurrencyFormatter)}
            </div>
          )}
          {showCartTest && showStrikethrough && !rescueGwaVariant && (
            <div className={styles.originalPriceValue}>
              {getCartV2CurrencyFormattedTotal(strikethroughValue, currencyFormatter, wholeNumberCurrencyFormatter)}
            </div>
          )}
          <div className={styles.priceValue}>
            {(() => {
              const base = showCartTest ? displayedFinalPrice : totalInCents;
              // rescue totals are read as a receipt that has to add up, so every row keeps its cents
              return rescueGwaVariant
                ? currencyFormatter.format(centsToDollarsNumber(base))
                : getCartV2CurrencyFormattedTotal(base, currencyFormatter, wholeNumberCurrencyFormatter);
            })()}
          </div>
          {!showCartTest && showActivationFeeDiscount && !rescueGwaVariant && (
            <div className={styles.dollarsOffValue}>
              {t('dollars_off', {
                amount: getCartV2CurrencyFormattedTotal(totalDiscount, currencyFormatter, wholeNumberCurrencyFormatter),
              })}
            </div>
          )}
          {showCartTest &&
            totalSavingsForBadge > 0 &&
            cartPricing.couponDiscount?.type !== 'free_trial' &&
            !rescueGwaVariant && (
              <div className={styles.dollarsOffValue}>
                {t('dollars_off', {
                  amount: getCartV2CurrencyFormattedTotal(
                    totalSavingsForBadge,
                    currencyFormatter,
                    wholeNumberCurrencyFormatter,
                  ),
                })}
              </div>
            )}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.priceData, { [styles.rescueSubtotal]: rescueGwaVariant })}>
      <div>{t('subtotal')}</div>
      <div className={styles.priceValue}>{currencyFormatter.format(centsToDollarsNumber(totalInCents))}</div>
    </div>
  );
}

function TaxPrice() {
  const { t } = useTranslation();
  const { currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const cartPricing = useCartPricing();
  const cartItemV2Enabled = useCartItemV2Enabled();
  const { taxInCents } = cartPricing;
  if (!taxInCents || cartItemV2Enabled) {
    return null;
  }

  return (
    <div className={classNames(styles.taxPrice, styles.priceData)}>
      <div>{t('tax')}</div>
      <div className={styles.pricingContainer}>
        <div className={styles.priceValue}>
          {getCartV2CurrencyFormattedTotal(taxInCents, currencyFormatter, wholeNumberCurrencyFormatter)}
        </div>
      </div>
    </div>
  );
}

/**
 * The rescue cart's itemized totals: what the membership lists at, what the adoption promo took off
 * it, and the one charge that is left.
 *
 * Under auto-apply nobody typed a code, so the cart is the only place the discount can be proved -
 * and a subtotal that simply arrives at $20 cannot show its work. Naming the promo as a signed line
 * against the list price is what makes a silent failure visible: if the coupon does not land, the
 * row is absent and the subtotal is wrong by exactly the amount that is missing.
 */
function RescueItemizedTotals() {
  const { t } = useTranslation();
  const cartPricing = useCartPricing();
  const { cart } = useContext(CheckoutContext);
  const { currency, currencyFormatter } = useLocale();
  const productSkuMap = useProductSkuMap();

  const { membershipInCents, hardwareInCents } = React.useMemo(
    () =>
      Object.values(cart.cartItems).reduce(
        (totals, cartItem) => {
          if (!isBundleCartItem(cartItem)) {
            return totals;
          }

          const subscriptionProduct = productSkuMap.get(cartItem.subscriptionLineItem.sku)?.product;
          const collarProduct = productSkuMap.get(cartItem.lineItem.sku)?.product;

          return {
            membershipInCents:
              totals.membershipInCents +
              (subscriptionProduct && types.isModuleSubscriptionProduct(subscriptionProduct)
                ? getLocalizedPricing(subscriptionProduct.pricingInCents, currency)
                : 0),
            hardwareInCents:
              totals.hardwareInCents +
              (collarProduct ? getLocalizedPricing(collarProduct.pricingInCents, currency) : 0),
          };
        },
        { membershipInCents: 0, hardwareInCents: 0 },
      ),
    [cart.cartItems, productSkuMap, currency],
  );

  if (!showRescuePdpRedesign()) {
    return null;
  }

  const format = (cents: number) => currencyFormatter.format(centsToDollarsNumber(cents));
  const promoDiscountInCents = appliedCouponDiscountInCents(cartPricing);
  const activationFeeInCents = cartPricing.activationFeesInCents ?? 0;

  return (
    <div className={styles.rescueTotals}>
      {/* only when the hardware is actually charged for - it is free in the adoption bundle, and a
          $0 row would be noise in a block whose job is to name real charges */}
      {hardwareInCents > 0 && (
        <div className={styles.rescueTotalsRow}>
          <span>{t('hardware_kit')}</span>
          <span>{format(hardwareInCents)}</span>
        </div>
      )}
      {membershipInCents > 0 && (
        <div className={styles.rescueTotalsRow}>
          <span>{t('membership')}</span>
          <span>{format(membershipInCents)}</span>
        </div>
      )}
      {promoDiscountInCents > 0 && (
        <div className={classNames(styles.rescueTotalsRow, styles.rescueTotalsDiscountRow)}>
          <span>{t('adoption_promo')}</span>
          <span>{t('negative_amount', { amount: format(promoDiscountInCents) })}</span>
        </div>
      )}
      {activationFeeInCents > 0 && (
        <>
          <div className={styles.rescueTotalsRow}>
            <span>{t('activation_fee')}</span>
            <span>{format(activationFeeInCents)}</span>
          </div>
          <div className={styles.rescueTotalsNote}>{t('one_time_not_part_of_your_membership')}</div>
        </>
      )}
    </div>
  );
}

/**
 * Shipping as a totals row rather than only a trust badge. It is the last cost an adopter cannot
 * otherwise rule out at the point they decide, and a totals block that omits it invites a guess.
 * The value comes from `useFreeShippingDisplay` - the same hook behind the badge - so the row and
 * the badge cannot disagree. Rescue only; every other cart still states shipping in the badge alone,
 * and `FreeShipping` drops its shipping badge here so the claim is not made twice.
 */
function ShippingPrice() {
  const { t } = useTranslation();
  const { isFreeShipping, shippingPrice } = useFreeShippingDisplay();

  if (!showRescuePdpRedesign()) {
    return null;
  }

  return (
    <div className={styles.shippingPrice}>
      <div>{t('shipping')}</div>
      <div className={classNames({ [styles.shippingFreeValue]: isFreeShipping })}>
        {isFreeShipping ? t('cart_item_v2.price_free') : shippingPrice}
      </div>
    </div>
  );
}

/**
 * The renewal disclosure, directly under the checkout button. The gifted months are the whole offer,
 * so the price that lands once they run out has to be stated at the point of commitment - before
 * this the cart carried no renewal line at all, and $189 appeared only on the PDP and in the FAQ.
 *
 * Set as one flat sentence with no icon and nothing bolded: sitting under the button it reads as
 * terms attached to the commitment, and emphasis on the figure made it read as a second price.
 *
 * Renders nothing when `useRescueRenewalPrice` cannot resolve a plan: a disclosure that quotes a
 * figure nothing backs is worse than no disclosure.
 *
 * The named variant carries the pet's name but no gendered pronoun. It used to select one through
 * ICU, which the l10n action's placeholder check cannot parse - it reads the English branch bodies
 * (`her`/`his`/`their`) as required placeholders and drops the whole key when a translation quite
 * correctly replaces them, so the key silently vanished from all 15 locales on every run. It was
 * also already inert in several: es-es selected `su` three ways and every French variant `son`.
 *
 * On the eligibility gate, not the redesign one. Both arms buy the same auto-renewing six-month
 * membership, so both have to be told what it renews at before they commit - a design experiment
 * must not decide whether a recurring charge is disclosed. Placement is the only thing the redesign
 * moved.
 */
function RenewalDisclosure() {
  const { t } = useTranslation();
  const renewalPrice = useRescueRenewalPrice();

  if (!showRescueGwaVariant() || !renewalPrice) {
    return null;
  }

  return (
    <div className={styles.renewalDisclosure}>
      {hasNanoVariantPetName()
        ? t('rescue_cart_renewal_named', { petName: getNanoVariantPetName(), price: renewalPrice })
        : t('rescue_cart_renewal_generic', { price: renewalPrice })}
    </div>
  );
}

/**
 * The cart's checkout navigation - logs the event, then routes to the unified or stripe checkout.
 * Exported so surfaces that render their own checkout button (the hardware-first summary and its
 * pinned bar) send shoppers down the same path as the shared one.
 */
export function useCartCheckout(onCheckOut: (cart: types.Cart, cartPricing: types.CartPricing) => void) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { cart } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const checkoutPaths = useCheckoutPaths();

  return useCallback(() => {
    onCheckOut(cart, cartPricing);
    // the in-app shop confirms on the member's stored card in a sheet, the same one the pdps open;
    // the stripe checkout page would ask a signed-in member for details the app already has
    if (isInAppShop()) {
      dispatch(storeCartActions.setShowInAppCheckout(true));
      return;
    }
    const useUnifiedCheckout = getFiGrowthBook().getFeatureValue('unified-checkout', false);
    history.push(useUnifiedCheckout ? checkoutPaths.UnifiedCheckout : checkoutPaths.StripeCheckout);
  }, [cart, cartPricing, checkoutPaths, dispatch, history, onCheckOut]);
}

/**
 * The totals block, in the order a receipt reads: tax, shipping, the subtotal, then what happens
 * after. Every call site renders the same rows - the mobile action sheet used to drift from the
 * desktop summary because each spelled the list out for itself.
 */
function CartTotals() {
  return (
    <>
      <TaxPrice />
      <RescueItemizedTotals />
      <ShippingPrice />
      <TotalPrice />
    </>
  );
}

export function PaymentOptions({ onCheckOut, showDigitalWallets, digitalWalletsOnly }: PaymentOptionsProps) {
  const { t } = useTranslation();
  const showApplePay = useShouldShowApplePay() && !getFiGrowthBook().getFeatureValue('express-checkout-wallets', false);
  const { applePayErrorAlertWhenAccountExists } = useCartMode();
  const { error, errorID, setError } = useError();
  const rescueGwaVariant = showRescuePdpRedesign();
  const checkout = useCartCheckout(onCheckOut);

  const onError = useCallback(
    (err: Error) => {
      setError(err.message);
    },
    [setError],
  );

  return (
    <div className={styles.cartPayment}>
      {!digitalWalletsOnly && <CartTotals />}
      <div className={styles.summaryActions}>
        {!digitalWalletsOnly && (
          <Button
            className={classNames(styles.checkoutButton, { [styles.rescueCheckoutButton]: rescueGwaVariant })}
            onClick={checkout}
          >
            {t('checkout')}
          </Button>
        )}
        {showApplePay && showDigitalWallets && (
          <ApplePayCheckout
            errorAlertOnAccountExists={applePayErrorAlertWhenAccountExists}
            events={events.cartPage}
            onError={onError}
          />
        )}
      </div>
      {!digitalWalletsOnly && <RenewalDisclosure />}
      {error && (
        <div className={styles.summaryError}>
          <ErrorMessage errors={[error]} errorID={errorID} />
        </div>
      )}
    </div>
  );
}

function FreeShipping() {
  const { t } = useTranslation();
  const { country } = useLocale();
  const { isFreeShipping, shippingPrice } = useFreeShippingDisplay();
  const hasFreeTrialBundle = useCartHasFreeTrialBundle();
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();
  const { returnPolicyCopy } = useCartMode();
  const isCanada = country === FiCountry.CA;
  const isCanadaFreeTrial = isCanada && hasFreeTrialBundle;

  const shippingText = onlyFreeTrials
    ? t('cancel_anytime')
    : isFreeShipping
      ? t('free_ground_shipping')
      : t('ground_shipping_price', { price: shippingPrice });

  // canada free trial users get different badges
  if (isCanadaFreeTrial) {
    return (
      <div className={styles.freeShippingContainer}>
        <div className={styles.freeShipping}>
          <IconReturns />
          {t('trust_badges.risk_free')}
        </div>
        <div className={styles.localShipping}>
          <span className={styles.localShippingIcon}>{'🇨🇦'}</span>
          {t('trust_badges.now_available_canada')}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.freeShippingContainer, { [styles.threeItems]: isCanada })}>
      <div className={styles.freeShipping}>
        <IconShipping />
        {shippingText}
      </div>
      {/* eligibility gate, not the redesign one: "risk free"/"30 day return" describes the free
          trial's terms, which are not the adoption's. an adopter must not be read those in either
          arm of a design experiment, so the row drops for all of them - the shipping badge above
          stays, because control has no totals row to carry it */}
      {!showRescueGwaVariant() && (
        <div className={styles.riskFree}>
          <IconReturns />
          {hasFreeTrialBundle
            ? t('free_for_days', { days: 30 })
            : t(returnPolicyCopy ? 'thirty_day_return_policy' : 'thirty_days_risk_free')}
        </div>
      )}
      {isCanada && (
        <div className={styles.localShipping}>
          <span className={styles.localShippingIcon}>{'🇨🇦'}</span>
          {t('canada_local_shipping')}
        </div>
      )}
    </div>
  );
}

function TermsOfServiceAndPrivacyPolicy() {
  const { t } = useTranslation();
  const { baseDomain } = getDomainConfig();
  return (
    <div className={styles.termsOfServiceAndPrivacyPolicy}>
      <Trans
        i18nKey="terms_of_service_and_privacy_policy"
        values={{
          terms: t('terms_of_service'),
          privacy: t('privacy_policy'),
        }}
        components={{
          terms: (
            <span
              className={styles.termsOfService}
              onClick={() => {
                window.open(`https://${baseDomain}/tos`, '_blank');
              }}
            />
          ),
          privacy: (
            <span
              className={styles.privacyPolicy}
              onClick={() => {
                window.open(`https://${baseDomain}/privacypolicy`, '_blank');
              }}
            />
          ),
        }}
      />
    </div>
  );
}

export default function CartActions({ onCheckOut, showDigitalWallets, allowLogin }: CartActionProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { cart, session } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const checkoutPaths = useCheckoutPaths();
  const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const expressCheckoutEnabled = getFiGrowthBook().getFeatureValue('express-checkout-wallets', false);
  // evaluate inline-wallet-checkout and inline-paypal-checkout on /bag to fire the exposure
  // event here, matching the funnel scope of express-checkout-wallets (cart -> purchase)
  // instead of only on /checkout/stripe
  getFiGrowthBook().getFeatureValue('inline-wallet-checkout', false);
  getFiGrowthBook().getFeatureValue('inline-paypal-checkout', false);
  const hasFreeTrialBundle = useCartHasFreeTrialBundle();
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();
  const rescueGwaVariant = showRescuePdpRedesign();

  return (
    <>
      <div className={styles.desktopCartActions}>
        {showEcomCartTest && !rescueGwaVariant && <FreeShipping />}
        {showEcomCartTest && (
          <div className={styles.desktopLoginButton}>
            {!session && allowLogin && (
              <div className={styles.loginButton}>
                <LoginButton returnTo={AppPaths.Bag} />
              </div>
            )}
          </div>
        )}
        <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={true} digitalWalletsOnly={false} />
        {hasFreeTrialBundle && onlyFreeTrials && (
          <div className={styles.freeTrialFooter}>
            {t('cart_item_v2.free_trial_footer', { duration: t('subscription_day', { days: 30 }) })}
          </div>
        )}
        {showEcomCartTest && (
          <div className={styles.desktopTermsOfServiceAndPrivacyPolicy}>
            <TermsOfServiceAndPrivacyPolicy />
          </div>
        )}
      </div>
      {/* rescue mobile: nothing pins. the totals, the action and the renewal disclosure all sit in
          the page flow under the cart, so the receipt is read in one pass instead of half of it
          being covered by a fixed bar. no trust badges - the adoption already carries the offer */}
      {rescueGwaVariant && (
        <div className={styles.mobileRescueSummary}>
          <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={showDigitalWallets} digitalWalletsOnly={false} />
        </div>
      )}
      <div className={styles.mobileFreeShipping}>{showEcomCartTest && !rescueGwaVariant && <FreeShipping />}</div>
      <div className={styles.footer}>
        {!session && allowLogin && (
          <>
            <div className={styles.loginButton}>
              <LoginButton returnTo={AppPaths.Bag} />
            </div>
            <TermsOfServiceAndPrivacyPolicy />
          </>
        )}
      </div>
      {!rescueGwaVariant && (
        <div className={styles.mobileActionSheet}>
          {showDigitalWallets && expressCheckoutEnabled && (
            <>
              <CartTotals />
              <Button
                type="button"
                onClick={() => {
                  onCheckOut(cart, cartPricing);
                  history.push(checkoutPaths.StripeCheckout);
                }}
              >
                {t('checkout')}
              </Button>
            </>
          )}
          {showDigitalWallets && !expressCheckoutEnabled && (
            <>
              <CartTotals />
              <SimpleDismissableModal trigger={<Button type="button">{t('checkout')}</Button>}>
                <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={true} digitalWalletsOnly={false} />
              </SimpleDismissableModal>
            </>
          )}
          {!showDigitalWallets && (
            <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={false} digitalWalletsOnly={false} />
          )}
          {hasFreeTrialBundle && onlyFreeTrials && (
            <div className={styles.freeTrialFooter}>
              {t('cart_item_v2.free_trial_footer', { duration: t('subscription_day', { days: 30 }) })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
